import { useEffect, useState, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { AGS_LOGO_URL } from "@/lib/brand";
import { SERVICES } from "@/data/services";

// Section dropdown items for "About" — anchor links into the landing page.
// Items with `to` navigate to a full route; items with `hash` scroll on the homepage.
const ABOUT_ITEMS = [
  { hash: "about", num: "01", label: "About Us" },
  { hash: "meaning", num: "02", label: "The Meaning Behind Our Brand" },
  { hash: "guarantee", num: "03", label: "Our Personal Guarantee" },
  { to: "/services", num: "04", label: "Our Services" },
  { hash: "site-leadership", num: "—", label: "Site Leadership Structure" },
  { hash: "team", num: "—", label: "Our Crew · In Action" },
  { hash: "why-ags", num: "05", label: "Why AGS Cleaning Services" },
  { hash: "process", num: "06", label: "How It Works" },
  { hash: "expertise", num: "07", label: "In-House Industrial & Hospital Expertise" },
  { hash: "green-pledge", num: "08", label: "Our Green Pledge" },
  { hash: "faq", num: "09", label: "FAQ" },
  { hash: "quote", num: "10", label: "Request a Quote" },
];

const PRIMARY = [
  { hash: "about", label: "About", dropdown: "about" },
  { hash: "services", label: "Services", dropdown: "services" },
  { to: "/facility-management", label: "Facility Management" },
  { to: "/process", label: "Process" },
  { to: "/expertise", label: "Expertise" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const isLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkHref = (hash) => (isLanding ? `#${hash}` : `/#${hash}`);

  const openDropdown = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(name);
  };
  const closeDropdown = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b border-ags-hairline transition-all duration-300 ${
        scrolled ? "bg-ags-cream/90 shadow-ags-ambient" : "bg-ags-cream/80"
      }`}
    >
      {/* Top utility strip */}
      <div data-testid="top-utility-bar" className="bg-[#0E2A57] text-white text-[11px] tracking-[0.04em]">
        <div className="container-ags h-8 flex items-center justify-between gap-4">
          <span className="hidden sm:inline-flex items-center gap-2 text-white/75">
            <span className="inline-block w-1.5 h-1.5 bg-[#2BB6E5] rounded-full" />
            Family-owned · Commercial cleaning across Ireland
          </span>
          <div className="ml-auto flex items-center gap-5">
            <Link
              to="/contact"
              data-testid="nav-top-contact"
              className="font-semibold uppercase tracking-[0.18em] text-white/85 hover:text-white transition-colors"
            >
              Contact Us
            </Link>
            <span className="text-white/30" aria-hidden>|</span>
            <Link
              to="/careers"
              data-testid="nav-hiring-link"
              className="inline-flex items-center gap-2 font-semibold uppercase tracking-[0.18em] text-[#BEE7F5] hover:text-white transition-colors"
            >
              <span className="inline-block w-1.5 h-1.5 bg-[#2BB6E5] rounded-full animate-pulse" />
              We're Hiring
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="container-ags h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <img src={AGS_LOGO_URL} alt="AGS Cleaning Services" className="h-12 md:h-14 w-auto object-contain" />
          <span className="hidden lg:inline-block h-8 w-px bg-ags-hairline" aria-hidden />
          <span className="hidden lg:inline-block text-[11px] uppercase tracking-[0.18em] text-[#0E2A57]/70 font-semibold">
            Family-Owned · Ireland
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {PRIMARY.map((l) =>
            l.dropdown === "about" ? (
              <div key={l.hash} className="relative" onMouseEnter={() => openDropdown("about")} onMouseLeave={closeDropdown}>
                <a
                  href={linkHref(l.hash)}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="nav-link text-sm font-medium tracking-wide text-[#050B14] inline-flex items-center gap-1"
                  aria-haspopup="true"
                  aria-expanded={activeDropdown === "about"}
                >
                  {l.label}
                  <ChevronDown size={14} className={`transition-transform ${activeDropdown === "about" ? "rotate-180" : ""}`} />
                </a>
                {activeDropdown === "about" && (
                  <div
                    data-testid="about-dropdown"
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[520px] bg-white border border-ags-hairline shadow-ags-hover p-3 flex flex-col gap-1"
                    onMouseEnter={() => openDropdown("about")}
                    onMouseLeave={closeDropdown}
                  >
                    <div aria-hidden className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-t border-l border-ags-hairline rotate-45" />
                    {ABOUT_ITEMS.map((it) => {
                      const commonCls =
                        "group flex items-center gap-3 px-3 py-2 hover:bg-ags-sand-soft transition-colors";
                      const inner = (
                        <>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#2BB6E5] font-semibold w-7 flex-shrink-0">
                            {it.num}
                          </span>
                          <span className="font-heading font-bold text-[13px] text-[#0E2A57] leading-tight group-hover:text-[#081A37]">
                            {it.label}
                          </span>
                        </>
                      );
                      if (it.to) {
                        return (
                          <Link
                            key={it.to}
                            to={it.to}
                            data-testid={`about-dropdown-${it.to.replace(/\//g, "")}`}
                            onClick={() => setActiveDropdown(null)}
                            className={commonCls}
                          >
                            {inner}
                          </Link>
                        );
                      }
                      return (
                        <a
                          key={it.hash}
                          href={linkHref(it.hash)}
                          data-testid={`about-dropdown-${it.hash}`}
                          onClick={() => setActiveDropdown(null)}
                          className={commonCls}
                        >
                          {inner}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : l.dropdown === "services" ? (
              <div key={l.hash} className="relative" onMouseEnter={() => openDropdown("services")} onMouseLeave={closeDropdown}>
                <Link
                  to="/services"
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="nav-link text-sm font-medium tracking-wide text-[#050B14] inline-flex items-center gap-1"
                  aria-haspopup="true"
                  aria-expanded={activeDropdown === "services"}
                >
                  {l.label}
                  <ChevronDown size={14} className={`transition-transform ${activeDropdown === "services" ? "rotate-180" : ""}`} />
                </Link>
                {activeDropdown === "services" && (
                  <div
                    data-testid="services-dropdown"
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[560px] bg-white border border-ags-hairline shadow-ags-hover p-3 grid grid-cols-2 gap-1"
                    onMouseEnter={() => openDropdown("services")}
                    onMouseLeave={closeDropdown}
                  >
                    <div aria-hidden className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-t border-l border-ags-hairline rotate-45" />
                    {SERVICES.map((s, i) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`dropdown-service-${s.slug}`}
                        onClick={() => setActiveDropdown(null)}
                        className="group flex items-start gap-3 px-3 py-2.5 hover:bg-ags-sand-soft transition-colors"
                      >
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#2BB6E5] font-semibold w-7 flex-shrink-0 mt-0.5">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="flex-1">
                          <span className="block font-heading font-bold text-[13px] text-[#0E2A57] leading-tight group-hover:text-[#081A37]">
                            {s.title}
                          </span>
                        </span>
                      </Link>
                    ))}
                    <div className="col-span-2 mt-2 pt-3 border-t border-ags-hairline px-3 flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold">
                        Ten disciplines · One standard
                      </span>
                      <Link
                        to="/services"
                        onClick={() => setActiveDropdown(null)}
                        className="text-[11px] uppercase tracking-[0.18em] text-[#0E2A57] font-bold hover:text-[#2BB6E5]"
                      >
                        View all →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="nav-link text-sm font-medium tracking-wide text-[#050B14]"
              >
                {l.label}
              </Link>
            )
          )}
          <Link to="/quote" data-testid="nav-cta-quote" className="bg-[#0E2A57] text-white hover:bg-[#081A37] px-6 py-3 text-sm font-semibold transition-colors">
            Request a Quote
          </Link>
        </nav>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((s) => !s)} className="md:hidden p-2 text-[#050B14]" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-ags-cream border-t border-ags-hairline max-h-[calc(100vh-7rem)] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-3">
            <div className="border-y border-ags-hairline -mx-6 px-6 py-2">
              <button onClick={() => setMobileAboutOpen((s) => !s)} className="w-full flex items-center justify-between py-2 text-base font-medium text-[#050B14]">
                <span>About</span>
                <ChevronDown size={18} className={`transition-transform ${mobileAboutOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileAboutOpen && (
                <div className="pl-2 pb-3 flex flex-col gap-1">
                  {ABOUT_ITEMS.map((it) => {
                    const inner = (
                      <>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#2BB6E5] font-semibold w-6">{it.num}</span>
                        <span className="text-sm text-[#0E2A57] font-medium">{it.label}</span>
                      </>
                    );
                    if (it.to) {
                      return (
                        <Link
                          key={it.to}
                          to={it.to}
                          onClick={() => { setOpen(false); setMobileAboutOpen(false); }}
                          className="flex items-center gap-3 py-2"
                        >
                          {inner}
                        </Link>
                      );
                    }
                    return (
                      <a
                        key={it.hash}
                        href={linkHref(it.hash)}
                        onClick={() => { setOpen(false); setMobileAboutOpen(false); }}
                        className="flex items-center gap-3 py-2"
                      >
                        {inner}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-b border-ags-hairline -mx-6 px-6 py-2">
              <button onClick={() => setMobileServicesOpen((s) => !s)} className="w-full flex items-center justify-between py-2 text-base font-medium text-[#050B14]">
                <span>Services</span>
                <ChevronDown size={18} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileServicesOpen && (
                <div className="pl-2 pb-3 flex flex-col gap-1">
                  {SERVICES.map((s, i) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => { setOpen(false); setMobileServicesOpen(false); }} className="flex items-center gap-3 py-2">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#2BB6E5] font-semibold w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm text-[#0E2A57] font-medium">{s.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* <Link to="/facility-management" onClick={() => setOpen(false)} className="text-base font-medium text-[#050B14] py-2">Facility Management</Link> */}
            <Link to="/facility-management" onClick={() => setOpen(false)} className="text-base font-medium text-[#050B14] py-2">Facility Management</Link>
            <Link to="/process" onClick={() => setOpen(false)} className="text-base font-medium text-[#050B14] py-2">Process</Link>
            <Link to="/expertise" onClick={() => setOpen(false)} className="text-base font-medium text-[#050B14] py-2">Expertise</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="text-base font-medium text-[#050B14] py-2">Contact Us</Link>
            <Link to="/careers" onClick={() => setOpen(false)} className="text-base font-medium text-[#0E2A57] py-2">We're Hiring →</Link>

            <Link to="/quote" onClick={() => setOpen(false)} className="bg-[#0E2A57] text-white px-6 py-3 text-sm font-semibold inline-block w-fit mt-3">
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
